import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import Apis from '@/Apis/_'
import { Auth } from '@finx/web-repository/src/services/Oidc'
import { convertMinutesToMs } from '@/utils/common'
import { LogoutConfig } from '@/types/interfaces/Logout'
import Env from '@finx/web-repository/src/services/Env'

const createDefaultConfig = (hospitalId: string): LogoutConfig => ({
  hospitalId,
  timeoutMs: convertMinutesToMs(90),
})

@Module({ namespaced: true, name: 'LogoutConfigStore' })
export default class LogoutConfigStore extends VuexModule {
  logoutConfig: LogoutConfig | null = null
  defaultTimer: number = 5400000

  get logoutConfigData() {
    return this.logoutConfig
  }

  get logoutTimeoutMs() {
    if (this.logoutConfig) {
      return this.logoutConfig.timeoutMs
    }
    return this.defaultTimer
  }

  @Mutation
  private setLogoutConfig(config: LogoutConfig | null) {
    this.logoutConfig = config
  }

  @Action({ rawError: true })
  async getLogoutConfig(hospitalId: string) {
    if (this.logoutConfig && this.logoutConfig.hospitalId === hospitalId) {
      return this.logoutConfig
    }

    try {
      const request = await Apis.Agendamento.LogoutApi.getLogoutConfigurationByHospitalId(hospitalId)
      if (request.success) {
        const timeoutMs = convertMinutesToMs(request.data.minutosInatividadeParaLogout)
        const config = {
          hospitalId,
          timeoutMs,
          ...request.data,
        }
        this.setLogoutConfig(config)
        return config
      }

      const defaultConfig = createDefaultConfig(hospitalId)
      this.setLogoutConfig(defaultConfig)
      return defaultConfig
    } catch (error) {
      const defaultConfig = createDefaultConfig(hospitalId)
      this.setLogoutConfig(defaultConfig)
      return defaultConfig
    }
  }

  @Action
  clearLogoutConfig() {
    this.setLogoutConfig(null)
  }

  @Action
  async authLogout(userId: string) {
    // IMPORTANTE: Se há impersonação ativa, encerrar sessão localmente e re-autenticar
    const isImpersonating = sessionStorage.getItem('IMPERSONATION_ACTIVE')
    if (isImpersonating === 'true') {
      // 1. Limpar flags ANTES de qualquer operação OIDC (desbloqueia o interceptor)
      sessionStorage.removeItem('IMPERSONATION_ACTIVE')
      sessionStorage.removeItem('IMPERSONATION_USER')
      sessionStorage.removeItem('IMPERSONATION_IN_PROGRESS')
      sessionStorage.removeItem('IMPERSONATION_REDIRECT')

      // 2. Limpar token do usuário personificado
      try {
        await Auth.userManager.removeUser()
      } catch (e) {
        // ignorar
      }

      // 3. Limpar demais dados locais
      localStorage.clear()
      sessionStorage.clear()

      // Limpar cookies
      const cookies = document.cookie.split(';')
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i]
        const eqPos = cookie.indexOf('=')
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT`
      }

      // 4. Re-autenticar com o usuário real (sessão Keycloak ainda válida)
      //    e redirecionar direto para a tela de personificação
      await Auth.login('/suporte/personificar')
      return
    }

    // Garantir que postLoginUrl não sobreviva ao round-trip do Keycloak
    localStorage.removeItem('postLoginUrl')

    // --- WORKAROUND KEYCLOAK (Fluxo Token-Exchange / Dasa) ---
    const user = await Auth.userManager.getUser()

    const logoutArgs: any = {
      post_logout_redirect_uri: `${Env.CLIENT_ADDRESS}/inicio`,
      extraQueryParams: {
        client_id: Env.KEYCLOAK_CLIENT_ID,
      },
    }

    if (user && user.id_token) {
      logoutArgs.id_token_hint = user.id_token
    }

    await Auth.userManager.signoutRedirect(logoutArgs)

    this.clearLogoutConfig()
  }
}
